/* eslint-disable max-lines */
import { Controller } from "stimulus";
import dispatchCustomEvent from "../../functions/dispatchCustomEvent.js";
import syncRowFieldNames from "../../functions/syncRowFieldNames.js";

export default class extends Controller {
  static get targets() {
    return ["checkbox", "selectAllCheckbox", "hiddenField", "row"];
  }

  static get values() {
    return { store: Boolean };
  }

  connect() {
    this._preventAutoComplete();
    this._updateSelectAllDisabledState();
    this._listenForSubmit();
    if (this.saveToStore) this._registerWithStore();
  }

  disconnect() {
    if (this._onSubmit) this.element.removeEventListener("submit", this._onSubmit);
  }

  updateSelectAll(event) {
    this._toggleSelectAllCheckbox();

    this._updateHiddenField(event);
    if (this.saveToStore) this._syncWithStore();
  }

  // Store methods

  _registerWithStore() {
    this.dispatch("register", {
      detail: { id: this.element.id, values: [] },
      prefix: false,
    });
  }

  _syncWithStore() {
    this.dispatch("sync", {
      detail: { id: this.element.id, values: this.hiddenFieldValueArr },
      prefix: false,
    });
  }

  updateFromStore({ detail: { id, values } }) {

    if (id !== this.element.id) return;

    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = values.includes(checkbox.dataset.id);
    });

    this._toggleSelectAllCheckbox();

    if (this.hasHiddenFieldTarget) {
      this.hiddenFieldTarget.value = values.join(",");
      dispatchCustomEvent(this.hiddenFieldTarget, "change");
    }
  }

  onSelectAll() {
    if (this.selectAllCheckboxTarget.checked) {
      this._selectAllCheckboxes();
      this._addValue();
    } else {
      this.selectedCheckboxValues.forEach((scv) => this._removeValue(scv));
      this._unSelectAllCheckboxes();
    }

    if (this.hasHiddenFieldTarget)
      dispatchCustomEvent(this.hiddenFieldTarget, "change");
    if (this.saveToStore) this._syncWithStore();
  }

  // Selected-rows-only submission
  //
  // A bulk `foo[][bar]` form renders one set of inputs per row and lets the server discard the
  // rows the user did not pick. hanamiUjs serialises every ajax form with `new FormData()`, so
  // the request is multipart whatever enctype it declares, and Rack's
  // `multipart_total_part_limit` (4096 parts, counting plain text fields) caps the fields one
  // request may carry. On a large page that cap is exceeded and Rack raises during param
  // parsing, before any action runs.
  //
  // Suppression happens HERE, once, at submit — not continuously as rows are ticked. Keeping a
  // row's `name` state in step with its checkbox state would be an invariant every path that
  // changes a checkbox has to maintain, and `form.reset()` (forms--ajax#onSuccess) is one such
  // path that cannot: it unchecks rows without firing `change`, which would leave them named
  // but unselected, i.e. posting a PARTIAL row. Deriving it at submit from live checkbox state
  // means there is no state to fall out of step.
  //
  // Self-gating on `row` targets: a form that marks its rows gets this, one that does not is
  // untouched. There is deliberately no opt-in flag to forget.
  _listenForSubmit() {
    if (!this.hasRowTarget) return;

    this._onSubmit = () => this._suppressUnselectedRows();
    this.element.addEventListener("submit", this._onSubmit);
  }

  _suppressUnselectedRows() {
    const suppressed = [];

    this.checkboxTargets.forEach((checkbox) => {
      if (checkbox.checked) return;

      const row = this._rowFor(checkbox);
      if (!row) return;

      suppressed.push(row);
      syncRowFieldNames(row, { submit: false });
    });

    if (suppressed.length === 0) return;

    // This listener is on the form, so it runs in the target phase — before the document-level
    // listener in hanamiUjs that builds the FormData. Restore on the next tick, once that has
    // read the DOM, so a form still on the page (a validation failure, say) stays usable.
    setTimeout(() => {
      suppressed.forEach((row) => syncRowFieldNames(row, { submit: true }));
    }, 0);
  }

  _rowFor(checkbox) {
    return checkbox.closest(`[data-${this.identifier}-target~="row"]`);
  }

  _toggleSelectAllCheckbox() {
    if (this.allCheckboxesChecked) {
      this._selectSelectAllCheckbox();
    } else {
      this._unselectSelectAllCheckbox();
    }
  }

  _updateHiddenField(event) {
    if (!this.hasHiddenFieldTarget) return;

    if (event.target.checked) {
      this._addValue();
    } else {
      this._removeValue(event.target.id);
    }

    dispatchCustomEvent(this.hiddenFieldTarget, "change");
  }

  _removeValue(valueToRemove) {
    if (!this.hasHiddenFieldTarget) return;

    const amendedValues = this.hiddenFieldValueArr.filter((a) => a !== valueToRemove);
    this.hiddenFieldTarget.value = amendedValues.join(",");
  }

  _addValue() {
    if (!this.hasHiddenFieldTarget) return;

    let selectedValues = new Set(this.selectedCheckboxValues);

    if (this.hiddenFieldTarget.value.length) {
      const hiddenFieldValueSet = new Set(this.hiddenFieldValueArr);
      selectedValues = selectedValues.union(hiddenFieldValueSet);
    }

    this.hiddenFieldTarget.value = [...selectedValues].join(",");
  }

  _selectSelectAllCheckbox() {
    this.selectAllCheckboxTarget.checked = true;
  }

  _unselectSelectAllCheckbox() {
    this.selectAllCheckboxTarget.checked = false;
  }

  _selectAllCheckboxes() {
    this.checkboxTargets.forEach((checkbox) => {
      if (!checkbox.disabled) {
        checkbox.checked = true;
      }
    });
  }

  _unSelectAllCheckboxes() {
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = false;
    });
  }

  _updateSelectAllDisabledState() {
    if (!this.hasSelectAllCheckboxTarget) return;

    const enabledCheckboxes = this.checkboxTargets.filter((checkbox) => !checkbox.disabled);
    this.selectAllCheckboxTarget.disabled = enabledCheckboxes.length === 0;

    if (this.selectAllCheckboxTarget.disabled) {
      this.selectAllCheckboxTarget.checked = false;
    }
  }

  get selectedCheckboxValues() {
    return this.selectedCheckboxes.map((checkbox) => {
      return checkbox.dataset.id;
    });
  }

  get selectedCheckboxes() {
    return this.checkboxTargets.filter((checkbox) => {
      return checkbox.checked;
    });
  }

  get allCheckboxesChecked() {
    const enabledCheckboxes = this.checkboxTargets.filter((checkbox) => !checkbox.disabled);
    return (
      enabledCheckboxes.length > 0 &&
      enabledCheckboxes.every((checkbox) => checkbox.checked)
    );
  }

  get hiddenFieldValueArr() {
    if (this.hiddenFieldTarget.value === "") return [];

    return this.hiddenFieldTarget.value.split(",");
  }

  get saveToStore() {
    return this.hasStoreValue && this.storeValue;
  }

  _preventAutoComplete() {
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.setAttribute("autocomplete", "off");
    });

    if (this.hasSelectAllCheckboxTarget) {
      this.selectAllCheckboxTarget.setAttribute("autocomplete", "off");
    }
  }
}
